import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpService } from '../shared/services/http.service';
import { SessionHandler } from '../shared/services/session-handler.service';
import { MdDialog, MdDialogRef } from '@angular/material';
import { dialogComponent } from '../dialog/dialog.component';
import { addNewAddressComponent } from '../trade/details/addNewAddress.component';
import { APPCONFIG } from '../config';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-foc-request',
  template: require('./foc_request.component.html'),
  styles: [require('./foc_request.component.scss')],
  providers: [HttpService, SessionHandler]
})
export class FocRequestComponent implements OnInit, OnDestroy {
  focRequestForm: FormGroup;
  submitted = false;

  // lists for dropdowns
  productCodes: Array<{ code: string; desc?: string }> = [];
  orderReasons: Array<{ code: string; text: string }> = [];

  // role / user data
  userData: any;
  isAdmin = false;
  isOTD = false;
  isEndConsumner = false;

  // search input (ngModel) + button state
  tradePartnerId: string = '';
  istradePartnerId: boolean = false;

  // address / trade data
  tradeData: any = {};
  billingData: any = {};
  addressList: any[] = [];
  selectedAddress: any = null;
  enableButtons: boolean = false;
  isD2CUser: boolean = false;

  createdBy: any = null;
  responsibleError: any = null;

  // useMock lets you develop without APIs
  useMock = true;

  dialogRef: any; // used for showing error dialogs

  // subscription holder (if you subscribe to shared address changes later)
  addressSub: Subscription;

  constructor(
    private fb: FormBuilder,
    private http: HttpService,
    private sessionHandler: SessionHandler,
    private router: Router,
    private dialog: MdDialog
  ) {}

  ngOnInit(): void {
    this.userData = this.safeParseSession('loginData');

    // set role flags if available
    if (this.userData && this.userData.role && Array.isArray(this.userData.role)) {
      this.isAdmin = this.userData.role.includes('ADMIN');
      this.isOTD = this.userData.role.includes('OTD_APPROVER');
      this.isEndConsumner = this.userData.role.includes('END_CONSUMER');
    }

    // build the reactive form: now includes customerNumber, pickupSame and pickupAddressId
    this.focRequestForm = this.fb.group({
      productCode: ['', Validators.required],
      customerNumber: ['', Validators.required],
      pickupSame: ['true', []],            // 'true' | 'false' as strings, matches RequestAdd behaviour
      pickupAddressId: [null, []],        // required only when pickupSame == 'false'
      shipTo: [{ value: '', disabled: true }],
      // match template: requestDate
      requestDate: [this.todayString(), [Validators.required, this.pastDateValidator, this.futureLimitValidator(30)]],
      orderReason: ['', Validators.required]
    });

    // react to pickupSame changes for validators & enabling/disabling pickupAddressId
    this.focRequestForm.get('pickupSame').valueChanges.subscribe(val => {
      this.onPickupSameChange(val);
    });

    this.loadProductCodes();
    this.loadOrderReasons();

    // If the user is non-admin / non-otd / non-endconsumer and session has tradePartnerId, prefill & fetch
    if (!this.isAdmin && !this.isOTD && !this.isEndConsumner) {
      const tp = this.userData && this.userData.tradePartnerId ? this.userData.tradePartnerId : null;
      if (tp) {
        this.focRequestForm.get('customerNumber').setValue(tp);
        this.tradePartnerId = tp;
        this.istradePartnerId = true;
        this.getTradeData();
      }
    }

    // default createdBy for non-otd/endconsumer
    if (!this.isOTD && !this.isEndConsumner && this.userData && this.userData.userName) {
      this.createdBy = this.userData.userName;
    }
  }

  ngOnDestroy(): void {
    if (this.addressSub) {
      this.addressSub.unsubscribe();
    }
  }

  // convenience getter for template
  get f(): { [key: string]: AbstractControl } { return this.focRequestForm.controls; }

  // ---------------- Loaders ----------------
  loadProductCodes() {
    if (this.useMock) {
      this.productCodes = [
        { code: 'P001', desc: 'Sample Product 1' },
        { code: 'P002', desc: 'Sample Product 2' },
        { code: 'P003', desc: 'Sample Product 3' }
      ];
      return;
    }

    // replace with your real endpoint later
    this.http.getRequest('product/v1/listFocProducts').subscribe(
      (res: any) => {
        const arr = this.normalizeItemsArray(res);
        this.productCodes = arr.map(p => ({ code: p.productCode || p.code, desc: p.description || p.desc }));
      },
      (err: any) => {
        console.error('loadProductCodes error', err);
        this.productCodes = [];
      }
    );
  }

  public loadOrderReasons() {
    if (this.useMock) {
      this.orderReasons = [{ code: 'R01', text: 'Sample reason' }];
      return;
    }

    this.http.getRequest('focApi/v1/getOrderReasons')
      .subscribe(
        res => {
          const items = (res && (res as any).orderReasons) ? (res as any).orderReasons as any[] : [];

          try {
            this.sessionHandler.saveItem('reasons', JSON.stringify(items));
          } catch (e) { /* ignore */ }

          this.orderReasons = items.map(r => ({ code: r.id || '', text: r.reason || '' }));

          this.http.onEnd && this.http.onEnd();
        },
        err => {
          if (err && err.status != 0) {
            this.dialogRef = this.dialog.open(dialogComponent);
            this.dialogRef.componentInstance.msg = err;
          }
          this.http.onEnd && this.http.onEnd();
        }
      );
  }

  // -----------------------------------------------------------------------------------------------

  // ---------------- Customer search / ship-to ----------------

  /**
   * Called when user types in the standalone ngModel input (or customerNumber form control is used).
   * Keeps button enablement consistent and resets ship data while typing.
   */
  tradePartnerIdEmptyheck() {
    // keep compatibility with any template code using this method
    this.resetData();
    if (this.tradePartnerId != null && this.tradePartnerId.trim() !== '') {
      this.istradePartnerId = true;
    } else {
      this.istradePartnerId = false;
    }
  }

  // additional helper when user types into the form control (keeps UX predictable)
  onCustomerNumberInput() {
    const val = this.focRequestForm.get('customerNumber').value;
    this.istradePartnerId = val && val.toString().trim() !== '';
    // clear previous trade/address state while typing
    this.tradeData = {};
    this.billingData = {};
    this.addressList = [];
    this.enableButtons = false;
    this.setShipTo('');
  }

  onCustomerNumberBackspace() {
    this.onCustomerNumberInput();
  }

  resetData() {
    // clear any previously populated ship-to
    this.setShipTo('');
    // keep form selections intact; only reset trade-specific UX bits
    this.tradeData = {};
    this.billingData = {};
    this.addressList = [];
    this.enableButtons = false;
  }

  /**
   * Search for trade partner and populate shipTo, billingData and addressList.
   */
  getTradeData() {
    // choose trade id from ngModel first, fallback to form control
    let tradeId = (this.tradePartnerId && this.tradePartnerId.trim() !== '') ? this.tradePartnerId.trim() : this.focRequestForm.get('customerNumber').value;

    if (!tradeId) {
      alert('Please enter a Customer Number to search');
      return;
    }

    // Example business guard from original code
    if (tradeId === '50032940' || (typeof tradeId === 'string' && tradeId.includes('50032940'))) {
      alert('Currently this service is not available for UK. Please contact admin for further information.');
      return;
    }

    // keep reactive control in sync
    this.focRequestForm.get('customerNumber').setValue(tradeId);
    this.tradePartnerId = tradeId;

    if (this.useMock) {
      // simulate lookup
      setTimeout(() => {
        // populate trade & billing & addresses (mock)
        this.tradeData = { tradePartnerId: tradeId, name1: 'Mock Co', name2: 'Ltd' };
        this.billingData = { invoiceAddress: '12 Mockingbird Lane, Mocksville' };
        this.addressList = [
          { id: 101, address: this.billingData.invoiceAddress, country: 'ML' },
          { id: 102, address: '45 Another St, Mocksville', country: 'ML' }
        ];
        this.enableButtons = true;
        this.setShipTo(this.billingData.invoiceAddress);
        this.setDefaultPickupAfterAddressLoad();
        this.istradePartnerId = true;
      }, 300);
      return;
    }

    // Real endpoint - defensive parsing
    this.http.getRequest('tradePartnerDetails/v1/get?tradePartnerId=' + encodeURIComponent(tradeId)).subscribe(
      (res: any) => {
        if (!res) {
          this.setShipTo('');
          alert('No trade data returned');
          return;
        }

        // Several possible shapes considered:
        if (res.status === 'Success' && res.tradePartner) {
          this.tradeData = res.tradePartner;
        } else if (res.tradePartner) {
          this.tradeData = res.tradePartner;
        } else {
          this.tradeData = res;
        }

        // After getting trade data, fetch address list (billing + shipping list)
        this.getAddressList();
        this.istradePartnerId = true;
      },
      (err: any) => {
        console.error('getTradeData error', err);
        this.setShipTo('');
        alert('Failed to retrieve trade data (see console).');
        if (this.http.onEnd) { try { this.http.onEnd(); } catch (e) {} }
      }
    );
  }

  /**
   * Called when customer number input loses focus - keep legacy behavior:
   * auto-populate shipTo if possible.
   */
  onCustomerNumberBlur() {
    const val = this.focRequestForm.get('customerNumber').value;
    if (!val || val.toString().trim() === '') {
      return;
    }
    this.tradePartnerId = val;
    this.tradePartnerIdEmptyheck();
    this.getTradeData();
  }

  // ---------------- Address list retrieval & handling ----------------

  public getAddressList() {
    let tradeId;
    if (this.isAdmin || this.isOTD || this.isEndConsumner) {
      tradeId = this.tradePartnerId && this.tradePartnerId.trim() !== '' ? this.tradePartnerId : (this.userData && this.userData.tradePartnerId ? this.userData.tradePartnerId : null);
    } else if (this.userData && this.userData.tradePartnerId) {
      tradeId = this.userData.tradePartnerId;
    }
    if (!tradeId) return;

    const data = {
      tradePartnerId: tradeId,
      salesOrgId: APPCONFIG.salesOrgId,
      isICBUser: false
    };

    this.http.postRequest(data, 'tradePartnerDetails/v1/invoiceBillingAddress')
      .subscribe(
        (res: any) => {
          this.saveAddressList(res);
        },
        (err: any) => {
          if (err && err.status != 0) {
            this.dialogRef = this.dialog.open(dialogComponent);
            this.dialogRef.componentInstance.msg = err;
          }
          this.http.onEnd && this.http.onEnd();
        }
      );
  }

  public saveAddressList(res: any) {
    if (!res) {
      this.addressList = [];
      this.billingData = {};
      this.setShipTo('');
      return;
    }

    if (res.billingAddress) {
      this.billingData = res.billingAddress;
    }

    if (res.shippingAddressList && res.shippingAddressList.length > 0) {
      this.addressList = res.shippingAddressList;
      this.enableButtons = true;
    } else {
      this.addressList = [];
      this.enableButtons = false;
      this.dialogRef = this.dialog.open(dialogComponent);
      this.dialogRef.componentInstance.msg = 'To create new request please save address details first.';
      this.http.onEnd && this.http.onEnd();
      // return early — no addresses to select from
    }

    // auto-populate shipTo with billing.invoiceAddress (preferred) else first shipping address
    if (this.billingData && this.billingData.invoiceAddress) {
      this.setShipTo(this.billingData.invoiceAddress);
    } else if (this.addressList.length > 0 && this.addressList[0].address) {
      this.setShipTo(this.addressList[0].address);
    } else {
      this.setShipTo('');
    }

    // decide default pickupSame state based on whether billing address matches a shipping address
    this.setDefaultPickupAfterAddressLoad();

    this.http.onEnd && this.http.onEnd();
  }

  private setDefaultPickupAfterAddressLoad() {
    if (this.billingData && this.addressList && this.addressList.length > 0) {
      const match = this.addressList.find(a => a.address === this.billingData.invoiceAddress);
      if (match) {
        // same address found -> disable pickupAddressId
        this.focRequestForm.get('pickupSame').setValue('true');
        this.focRequestForm.get('pickupAddressId').disable();
        this.focRequestForm.get('pickupAddressId').setValue(null);
        return;
      }
    }
    // otherwise set pickupSame to false and enable selection (default to last address)
    this.focRequestForm.get('pickupSame').setValue('false');
    this.focRequestForm.get('pickupAddressId').enable();
    if (this.addressList.length > 0) {
      this.focRequestForm.get('pickupAddressId').setValue(this.addressList[this.addressList.length - 1].id);
    } else {
      this.focRequestForm.get('pickupAddressId').setValue(null);
    }
  }

  public onChange(selectedId: any) {
    if (!selectedId) return;
    const found = this.addressList.find(a => String(a.id) === String(selectedId));
    if (found) {
      // optionally do country handling, etc.
    }
  }

  public saveNewAddress() {
    let tradeId;
    if (this.isAdmin || this.isOTD || this.isEndConsumner) {
      tradeId = this.tradePartnerId && this.tradePartnerId.trim() !== '' ? this.tradePartnerId : (this.userData && this.userData.tradePartnerId ? this.userData.tradePartnerId : null);
    } else if (this.userData && this.userData.tradePartnerId) {
      tradeId = this.userData.tradePartnerId;
    }

    this.dialogRef = this.dialog.open(addNewAddressComponent);
    this.dialogRef.componentInstance.tradeId = tradeId;
    // after closing the dialog, ideally the dialog will update addresses via your commonService.addressChange
    // If you want immediate refresh here, subscribe to afterClosed() and call getAddressList()
    this.dialogRef.afterClosed().subscribe(() => {
      // small delay to allow saved address to be available in backend
      setTimeout(() => { this.getAddressList(); }, 300);
    });
  }

  // ---------------- ShipTo helper + format ----------------
  private setShipTo(text: string) {
    const ctrl = this.focRequestForm.get('shipTo');
    ctrl.enable();
    ctrl.setValue(text || '');
    ctrl.disable();
  }

  private formatAddress(addrObj: any): string {
    if (!addrObj) { return ''; }
    const parts: string[] = [];
    if (addrObj.street) parts.push(addrObj.street);
    if (addrObj.city) parts.push(addrObj.city);
    if (addrObj.postalCode) parts.push(addrObj.postalCode);
    if (addrObj.zip) parts.push(addrObj.zip);
    if (addrObj.country) parts.push(addrObj.country);
    if (addrObj.address) parts.push(addrObj.address);
    return parts.filter(Boolean).join(', ');
  }

  // ---------------- Pickup same handling ----------------
  public onPickupSameChange(val: any) {
    // val expected to be 'true' or 'false' (string) based on template; handle boolean also
    const isSame = (val === 'true' || val === true);
    if (isSame) {
      // clear/disable pickupAddressId
      this.focRequestForm.get('pickupAddressId').clearValidators();
      this.focRequestForm.get('pickupAddressId').updateValueAndValidity();
      this.focRequestForm.get('pickupAddressId').setValue(null);
      this.focRequestForm.get('pickupAddressId').disable();

      // set shipTo from billing address when same
      if (this.billingData && this.billingData.invoiceAddress) {
        this.setShipTo(this.billingData.invoiceAddress);
      }
    } else {
      // enable and require pickupAddressId
      this.focRequestForm.get('pickupAddressId').setValidators([Validators.required]);
      this.focRequestForm.get('pickupAddressId').updateValueAndValidity();
      this.focRequestForm.get('pickupAddressId').enable();
      if (!this.focRequestForm.get('pickupAddressId').value && this.addressList.length > 0) {
        this.focRequestForm.get('pickupAddressId').setValue(this.addressList[this.addressList.length - 1].id);
      }
    }
  }

  // ---------------- Date validators ----------------

  todayString(): string {
    const d = new Date();
    const mm = ('0' + (d.getMonth() + 1)).slice(-2);
    const dd = ('0' + d.getDate()).slice(-2);
    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  pastDateValidator(control: AbstractControl) {
    if (!control.value) { return null; }
    const sel = new Date(control.value); sel.setHours(0,0,0,0);
    const today = new Date(); today.setHours(0,0,0,0);
    return sel < today ? { pastDate: true } : null;
  }

  futureLimitValidator(nDays: number) {
    return (control: AbstractControl) => {
      if (!control.value) return null;
      const sel = new Date(control.value); sel.setHours(0,0,0,0);
      const today = new Date(); today.setHours(0,0,0,0);
      const limit = new Date(today.getTime() + nDays * 24 * 60 * 60 * 1000);
      return sel > limit ? { futureLimit: true } : null;
    };
  }

  // ---------------- Submit ----------------

  onSubmit() {
    this.submitted = true;
    if (this.focRequestForm.invalid) {
      Object.keys(this.focRequestForm.controls).forEach(k => this.focRequestForm.get(k).markAsTouched());
      return;
    }

    const form = this.focRequestForm.getRawValue();
    const payload: any = {
      productCode: form.productCode,
      soldTo: form.customerNumber,
      shipToAddress: this.focRequestForm.get('shipTo').value,
      requestDate: form.requestDate,
      orderReason: form.orderReason,
      pickupSame: form.pickupSame,
      pickupAddressId: form.pickupAddressId,
      createdBy: this.userData ? this.userData.email : null
    };

    if (this.useMock) {
      // mock flow: pretend stock check + create
      console.debug('Mock: creating FOC for', payload.productCode, 'soldTo', payload.soldTo);
      setTimeout(() => {
        const mockStockRes: any = { available: true };
        if (!mockStockRes.available) {
          alert(mockStockRes.message || 'Product not available (mock)');
          return;
        }
        setTimeout(() => {
          alert('Mock: FOC Request created successfully');
          this.router.navigate(['/app/focdashboard']);
        }, 250);
      }, 400);
      return;
    }

    // real flow when ready: check stock then create
    const stockPayload = { productCode: payload.productCode, soldTo: payload.soldTo };
    this.http.postRequest(stockPayload, 'sap/v1/checkStockAvailability').subscribe(
      (stockRes: any) => {
        const available = (stockRes && (stockRes.available === true || stockRes.available == 'true')) || (stockRes === true);
        if (!available) {
          const message = stockRes && stockRes.message ? stockRes.message : 'Product not available';
          alert(message);
          return;
        }
        this.http.postRequest(payload, 'foc/v1/createFOCRequest').subscribe(
          (res: any) => {
            const success = res && (res.status === 'Success' || res.responseStatus === 'Success' || res.success === true);
            if (success) {
              alert('FOC Request created successfully');
              this.router.navigate(['/app/focdashboard']);
            } else {
              alert(res && res.errorMessage ? res.errorMessage : 'Failed to create FOC request');
            }
          },
          (err: any) => {
            console.error('createFOCRequest error', err);
            alert('Failed to create FOC request. Check console.');
          }
        );
      },
      (err: any) => {
        console.error('checkStockAvailability error', err);
        alert('Stock check failed. Check console.');
      }
    );
  }

  // ---------------- Utils ----------------
  private normalizeItemsArray(res: any): any[] {
    if (!res) return [];
    if (Array.isArray(res)) return res as any[];
    if (res.items && Array.isArray(res.items)) return res.items;
    if (res.data && Array.isArray(res.data)) return res.data;
    return [res];
  }

  private safeParseSession(key: string) {
    try {
      const v = this.sessionHandler.getItem(key);
      return v ? JSON.parse(v) : null;
    } catch (e) {
      return null;
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/foc_request/foc_request.component.ts